import * as React from 'react'
import {Dialog, DialogTitle, DialogContent, IconButton} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

import {FONT, SIZE, WEIGHT} from '../../../styles/foundation/typography'
import {NAVY, SUBTLE, BORDER, PRIMARY_BLUE} from '../../../styles/foundation/colors'

import SopPage from './SopPage'

/**
 * Weight Freeze (v2) — the SoP as an overlay.
 *
 * ONE SOURCE, NOT A COPY. The body of this dialog is `SopPage` itself, mounted
 * as a child. There is nothing to extract: SopPage's own header, its
 * account-standing banner and its footer CTA were all removed on request (see
 * its comments), so what it renders today IS the procedure and nothing else —
 * pulling that out into a `SopBody` would create a second file whose only
 * content is a one-line re-export from the first. Rendering the page component
 * is what guarantees the route at `/weight-freeze-v2` and this dialog can never
 * drift, which is the whole reason the route is safe to keep.
 *
 * Its dataset comes in as props rather than through SopPage's frozen defaults,
 * so the step counters read the same live store the page behind the dialog does
 * — a catalogue merge or a request raised earlier in the session is reflected
 * here exactly as it is on the routed page.
 *
 * Every accessibility requirement is MUI's Dialog doing its job, not something
 * hand-rolled: `aria-labelledby` points at the DialogTitle below, focus is
 * trapped inside the paper while it is open, Escape calls `onClose`, and on
 * close focus is restored to the element that opened it. That last one only
 * holds while the trigger is still on screen — the one case where it is not is
 * a step link, which navigates the page out from under the dialog, and the
 * caller handles that with the module's usual focus backstop (InitiatePage).
 */

/** The id `aria-labelledby` points at. Fixed rather than generated: only one SoP dialog can be open. */
const TITLE_ID = 'freeze-sop-dialog-title'

/**
 * True px, deliberately. A Dialog is portalled to document.body, so it renders
 * OUTSIDE the module container's `zoom: 0.8` (index.js) — the same reason every
 * drawer in this module states its own width in real pixels.
 *
 * The number is what SopPage's widest row needs before the cap it sets on its
 * own step panel (SOP_PANEL_MAX, 800) stops being the thing that decides the
 * measure: 800 for the panel, plus the step card's 41px detail indent and 16px
 * of padding on the far side, plus the 38px number rail and the gap after it,
 * plus the card's own borders and this dialog's content padding. Narrower and
 * the dialog clips the panel instead of the panel setting its own measure.
 */
const DIALOG_MAX_W = 980

/**
 * Margin, and the width/height calcs that have to agree with it. MUI's own
 * `fullWidth` hardcodes `calc(100% - 64px)` against a 32px margin, which is
 * more than a phone can spare, so both halves are restated per breakpoint here
 * rather than letting the margin shrink under a width that assumed the old one.
 */
const DIALOG_M = {xs: 1.5, sm: 4}
const DIALOG_FIT = {xs: 'calc(100% - 24px)', sm: 'calc(100% - 64px)'}

export default function SopDialog({open, onClose, onNavigate, skus, requests, subcats, packs}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby={TITLE_ID}
      // MUI's breakpoint caps are theme values that know nothing about the
      // measure SopPage sets for itself; DIALOG_MAX_W above is that number.
      maxWidth={false}
      PaperProps={{sx: {m: DIALOG_M, width: DIALOG_FIT, maxWidth: DIALOG_MAX_W, maxHeight: DIALOG_FIT}}}
    >
      {/* The title row stays put while the body scrolls — `scroll='paper'`, the
          Dialog default — so the close control is reachable from anywhere in a
          procedure this long without scrolling back to the top for it. */}
      <DialogTitle
        id={TITLE_ID}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.md, color: NAVY,
          py: 1.5, borderBottom: `1px solid ${BORDER}`,
        }}
      >
        {/* Same glyph as the button that opens this, so the overlay is visibly
            the thing that was clicked rather than an unrelated panel. */}
        <InfoOutlinedIcon aria-hidden='true' sx={{fontSize: 18, color: PRIMARY_BLUE}} />
        Weight freeze SOP
        <IconButton onClick={onClose} aria-label='Close the procedure' size='small' sx={{ml: 'auto', color: SUBTLE}}>
          <CloseIcon fontSize='small' />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{p: {xs: 1.5, sm: 2.5}}}>
        <SopPage onNavigate={onNavigate} skus={skus} requests={requests} subcats={subcats} packs={packs} />
      </DialogContent>
    </Dialog>
  )
}
